import React, { useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import produce from 'immer';
import { modeUIContext,
    selectSlideOutPanelOptions,
    showSlideOutPanel,
    SlideOutPanelLocation }
    from '@moderepo/mode-ui-state';
import { getAgentFeaturePermissions, ProjectAgent, Agent } from '@moderepo/mode-internal-apis';
import { fetchProjectAgents, selectLoggedInAgent,
    selectProjectAgent, selectProjectAgents, selectProjectById, ritzDataContext } from '@moderepo/mode-data-state-ritz';
import { useSearchParams, capitalizeAll, BaseListCompField, compareObjects, compareNumbers,
    compareStrings, compareTimestamps, compareBooleans, GenericMessageBox } from '@moderepo/mode-app-components';
import { AgentsTableProps, getAgentRoleName, AgentsTable, AgentTableDataItem } from '@moderepo/mode-app-components-internal';
import { SortOrder } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { SlideOutPanelCompType, isAgentPreviewCompOptions, AgentPreviewCompOptions } from '../routes/defaultProjectConfig';


interface AgentSearchParams {
    readonly sortField: string;
    readonly sortOrder: SortOrder;
}


/**
 * This is the page where we show all the agents belonged to the selected project
 */
export const AgentManagePage: React.FC = () => {
    const params = useParams();
    const projectId = Number(params.projectId);

    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const project = selectProjectById(dataState, projectId);
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const agents = selectProjectAgents(dataState, projectId);
    const { trans } = useRitzTranslation(project);
    const permissions = getAgentFeaturePermissions(projectAgent);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const [selectedAgentId, setSelectedAgentId] = useState <number>();

    const {
        searchParams, onOtherFieldsChange,
    } = useSearchParams<AgentSearchParams>({
        searchText  : '',
        itemsPerPage: 0,
        currentPage : 0,
        otherFields : {
            sortField: 'agentId',
            sortOrder: SortOrder.ASC,
        },
    });

    const sortedAgents = useMemo(() => {
        if (agents) {
            // The mapping of fieldName => compareFunction. The compareObjects function doesn't know the value type of the field therefore
            // we need to tell it which function to use.
            const compareFunctionMapping = {
                agentId        : compareNumbers,
                name           : compareStrings,
                email          : compareStrings,
                role           : compareStrings,
                creationTime   : compareTimestamps,
                verified       : compareBooleans,
                auth2FActivated: compareBooleans,
            };

            return [...agents].sort((agent1: ProjectAgent, agent2: ProjectAgent): number => {

                const ascCompareResult = compareObjects(agent1, agent2, searchParams.otherFields.sortField, compareFunctionMapping);

                // Compare the 1 data using ASCENDING order and then multiply the result with -1 if the sort order is DESC
                return ascCompareResult * (searchParams.otherFields && searchParams.otherFields.sortOrder === SortOrder.DESC ? -1 : 1);
            });
        }

        return undefined;
    }, [agents, searchParams.otherFields]);


    useEffect(() => {
        // reload agent on each page load because the cached data might not be valid anymore
        dataDispatch(fetchProjectAgents(projectId)).catch(() => { /* ignore error */ });
    }, [projectId, dataDispatch]);


    // This useEffect is used for checking the slideOutPanelOptions to see we are showing User Preview
    // if we are, automatically set it as the selectedUserId
    useEffect(() => {
        if (isAgentPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.agentId !== undefined) {
            setSelectedAgentId(slideOutPanelOptions.compProps.data.agentId);
        } else {
            setSelectedAgentId(undefined);
        }
    }, [setSelectedAgentId, slideOutPanelOptions]);


    /**
     * Localize the member data before passing it to the Member Component for display
     */
    const localizeAgentsData = useCallback((projectAgents: readonly ProjectAgent[] | undefined): readonly AgentTableDataItem[] | undefined => {
        if (projectAgents) {
            return projectAgents.map((agent: ProjectAgent): AgentTableDataItem => {
                return {
                    actualValue : agent,
                    displayValue: {
                        verified: capitalizeAll(trans(agent.verified ? 'label.verified' : 'label.not_verified')),
                        agentId : agent.agentId.toString(),
                        name    : agent.name ? agent.name : capitalizeAll(trans('label.none')),
                        email   : agent.email ? agent.email : capitalizeAll(trans('label.none')),
                        role    : capitalizeAll(trans(getAgentRoleName(agent.role))),
                    },
                    isLoggedInAgent: loggedInAgent !== undefined && agent.agentId === loggedInAgent.id,
                    selected       : agent.agentId === selectedAgentId,
                };
            });
        }

        return undefined;
    }, [trans, loggedInAgent, selectedAgentId]);


    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);

    // This useEffect is used for checking the slideOutPanelOptions to see we are showing Agent Preview
    // if we are, automatically set it as the setSelectedAgentId
    useEffect(() => {
        if (isAgentPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data?.agentId !== undefined) {
            setSelectedAgentId(slideOutPanelOptions.compProps.data.agentId);
        } else {
            setSelectedAgentId(undefined);
        }
    }, [slideOutPanelOptions, setSelectedAgentId]);


    // assigns "preview" agent as current Agent, to pass into agentInfoContainer
    const onPreviewAgentClicked = useCallback((agent: ProjectAgent | undefined) => {

        const options: AgentPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.AGENT_PREVIEW,
            compProps: {
                data: {
                    projectId,
                    agentId: agent?.agentId,
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [projectId, uiDispatch]);

    /**
     * On onCreateAgentClicked, we will unselect the current selected agent if there is one selected
     */
    const onCreateAgentClicked = useCallback(() => {
        onPreviewAgentClicked(undefined);
    }, [onPreviewAgentClicked]);


    const onItemSelected = useCallback((agent: ProjectAgent) => {
        onPreviewAgentClicked(agent);
        setSelectedAgentId(agent?.agentId);
    }, [onPreviewAgentClicked]);

    /**
     * useMemo to store FieldSettings to avoid re-rendering
     */
    const fieldSettings = useMemo(() => {
        return {
            fields: {
                avatar: {
                    label: '',
                },
                agentId: {
                    dataItemProp: 'agentId',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : agents && agents.length > 0,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.name')),
                    sortable    : agents && agents.length > 0,
                },
                email: {
                    dataItemProp: 'email',
                    label       : capitalizeAll(trans('label.email')),
                    sortable    : agents && agents.length > 0,
                },
                role: {
                    dataItemProp: 'role',
                    label       : capitalizeAll(trans('label.role')),
                    sortable    : agents && agents.length > 0,
                },
                verified: {
                    dataItemProp: 'verified',
                    label       : capitalizeAll(trans('label.verified')),
                    sortable    : agents && agents.length > 0,
                },
            },
            onSortByField,
            currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
            currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
        };
    }, [agents, onSortByField, searchParams.otherFields, trans]);

    const customPrimaryAction = useMemo((): AgentsTableProps['customPrimaryActions'] => {
        if (permissions.canAddAgents) {
            return [{
                label  : capitalizeAll(trans('label.add')),
                icon   : 'add',
                variant: 'contained',
                color  : 'primary',
                onClick: onCreateAgentClicked,
            }];
        }

        return [];
    }, [onCreateAgentClicked, permissions.canAddAgents, trans]);

    const text = useMemo(() => {
        return {
            you: trans('label.you'),
        };
    }, [trans]);


    return (
        <div className="page-main-content">
            {agents && (
                <>
                    <div className="title-bar">
                        <div className="title-bar-item title-text">
                            <div className="page-title">
                                {trans('page.title.agents')}
                            </div>
                            <div className="page-subtitle">
                                {trans('page.subtitle.agents')}
                            </div>
                        </div>
                    </div>

                    {permissions.canViewAgents && (
                        <AgentsTable
                            listData={localizeAgentsData(sortedAgents)}
                            title={capitalizeAll(trans('label.agent', { count: 2 }))}
                            icon="headset_mic"
                            texts={text}
                            onListItemSelected={onItemSelected}
                            customPrimaryActions={customPrimaryAction}
                            fieldsSettings={fieldSettings}
                            noDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.agent', { count: 2 })),
                                    })}
                                />
                            )}

                            emptyDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.agent', { count: 2 })),
                                    })}
                                />
                            )}

                        />
                    )}

                </>
            )}

            {!agents && (
                <CircularProgress />
            )}
        </div>
    );
};
